import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'

import { api, ApiError } from '@/lib/api'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Select } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Toast, ToastDescription, ToastTitle } from '@/components/ui/toast'
import type { ConversationListResponse } from '@sdr-agend/admin-schemas'
import type { User } from '@sdr-agend/admin-schemas'

interface ToastState {
  open: boolean
  variant: 'default' | 'destructive'
  title: string
  description?: string
}

interface CarteiraDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** A SDR cuja carteira está sendo inspecionada. */
  sdr: User | null
  /** Todas as SDRs ativas — populam o picker de reatribuição (exclui a própria). */
  sdrs: ReadonlyArray<User>
}

/**
 * Story 12.6 (Epic E12) — lista os leads da carteira de uma SDR
 * (`GET /admin/conversations?sdrId=X`, já existente desde a Story 12.4) e
 * permite reatribuir individualmente pra outra SDR
 * (`PATCH /admin/customers/:id/assigned-sdr`, admin/manager only).
 *
 * Reaproveita a listagem de conversas em vez de criar um endpoint novo de
 * "listar clientes por SDR" — a carteira de uma SDR é, na prática, o
 * conjunto de conversas que já aparecem nesse filtro.
 */
export function CarteiraDialog({
  open,
  onOpenChange,
  sdr,
  sdrs,
}: CarteiraDialogProps): React.ReactElement | null {
  const queryClient = useQueryClient()
  const [toast, setToast] = useState<ToastState>({ open: false, variant: 'default', title: '' })
  const [reassigning, setReassigning] = useState<string | null>(null)

  const { data, isLoading, isError } = useQuery<ConversationListResponse>({
    queryKey: ['admin', 'conversations', 'sdr-carteira', sdr?.id],
    queryFn: async () =>
      api<ConversationListResponse>(
        `/admin/conversations?sdrId=${sdr?.id ?? ''}&perPage=100`,
      ),
    enabled: open && sdr !== null,
  })

  const reassignMutation = useMutation({
    mutationFn: async ({ customerId, toSdrId }: { customerId: string; toSdrId: string }) => {
      await api(`/admin/customers/${customerId}/assigned-sdr`, {
        method: 'PATCH',
        body: JSON.stringify({ sdrId: toSdrId }),
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'conversations'] })
      // BACKLOG-LOW-12.6-F1 — Conversations.tsx (tela principal) cacheia sob
      // a chave string 'admin-conversations', não o array ['admin',
      // 'conversations'] acima (inconsistência de convenção pré-existente
      // no app). Sem isto, reatribuir um lead aqui não atualizava a tela
      // principal até o próximo refetch natural.
      void queryClient.invalidateQueries({ queryKey: ['admin-conversations'] })
      setToast({ open: true, variant: 'default', title: 'Lead reatribuído com sucesso.' })
    },
    onError: (err: unknown) => {
      const message =
        err instanceof ApiError
          ? `Erro ${err.status}: ${err.bodyText}`
          : err instanceof Error
            ? err.message
            : 'Erro desconhecido.'
      setToast({ open: true, variant: 'destructive', title: 'Falha ao reatribuir lead', description: message })
    },
    onSettled: () => setReassigning(null),
  })

  if (sdr === null) return null

  const otherSdrs = sdrs.filter((s) => s.id !== sdr.id)
  const items = data?.items ?? []

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Carteira de {sdr.name}</DialogTitle>
            <DialogDescription>
              Leads atribuídos a esta SDR. Reatribua individualmente pra outra SDR ativa quando
              necessário (ex.: SDR saiu da empresa).
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[60vh] overflow-y-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lead</TableHead>
                  <TableHead>Campanha</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-56 text-right">Reatribuir para</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-sm text-muted-foreground">
                      Carregando…
                    </TableCell>
                  </TableRow>
                )}
                {isError && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-sm text-destructive">
                      Erro ao carregar a carteira.
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && !isError && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-sm text-muted-foreground">
                      Nenhum lead atribuído a esta SDR ainda.
                    </TableCell>
                  </TableRow>
                )}
                {items.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>
                      <div className="font-medium">{c.customerName ?? '—'}</div>
                      <div className="text-xs text-muted-foreground">{c.customerPhone}</div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {c.campaignName ?? '—'}
                    </TableCell>
                    <TableCell className="text-sm">{c.status}</TableCell>
                    <TableCell className="text-right">
                      <Select
                        disabled={otherSdrs.length === 0 || reassignMutation.isPending}
                        value=""
                        onChange={(e) => {
                          const toSdrId = e.target.value
                          if (toSdrId === '') return
                          setReassigning(c.customerId)
                          reassignMutation.mutate({ customerId: c.customerId, toSdrId })
                        }}
                      >
                        <option value="">
                          {otherSdrs.length === 0 ? 'Nenhuma outra SDR ativa' : '— selecione —'}
                        </option>
                        {otherSdrs.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </Select>
                      {reassigning === c.customerId && reassignMutation.isPending && (
                        <Loader2 className="ml-2 inline-block h-4 w-4 animate-spin" aria-hidden="true" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Toast
        open={toast.open}
        variant={toast.variant}
        onOpenChange={(open) => setToast((s) => ({ ...s, open }))}
      >
        <ToastTitle>{toast.title}</ToastTitle>
        {toast.description !== undefined && <ToastDescription>{toast.description}</ToastDescription>}
      </Toast>
    </>
  )
}
